/* <policy-toc>
   Builds the TOC sidebar by scanning the rendered article for <h2> elements,
   slugifying IDs, and running IntersectionObserver scroll-spy.

   Observed children:
     [data-toc-list]    Empty <ul> populated on connectedCallback.
     [data-policy-body] Article container holding policy.body HTML.

   Behavior:
     1. connectedCallback: query [data-policy-body] for all <h2>; for each:
        - assign h2.id = 'sec-' + slugify(h2.textContent)
        - append <li><a href="#sec-..."> to [data-toc-list]
     2. TOC link click: smooth-scroll to target with 130px sticky-header offset.
     3. IntersectionObserver watches all h2; topmost intersecting h2's TOC link
        gets [data-active].
     4. If [data-show-toc] === 'false' OR <h2> count is 0, collapse the sidebar
        by adding .page-policy__wrap--no-toc class to the wrapper.

   Dispatched events: none.
   Consumed events: none.
*/
class PolicyToc extends HTMLElement {
  connectedCallback() {
    if (this.dataset.showToc === 'false') return;
    this._article = this.querySelector('[data-policy-body]');
    this._tocList = this.querySelector('[data-toc-list]');
    if (!this._article || !this._tocList) return;

    const h2s = Array.from(this._article.querySelectorAll('h2'));
    if (h2s.length === 0) {
      const wrap = this.querySelector('.page-policy__wrap');
      if (wrap) wrap.classList.add('page-policy__wrap--no-toc');
      return;
    }

    h2s.forEach((h2, i) => {
      if (!h2.id) h2.id = 'sec-' + this._slug(h2.textContent) + '-' + i;
      const li = document.createElement('li');
      const a = document.createElement('a');
      a.href = '#' + h2.id;
      a.textContent = h2.textContent;
      a.addEventListener('click', (e) => this._onTocClick(e, h2));
      li.appendChild(a);
      this._tocList.appendChild(li);
    });

    this._observe(h2s);
  }

  _slug(s) {
    return s.toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .trim()
      .replace(/\s+/g, '-')
      .slice(0, 60);
  }

  _onTocClick(e, target) {
    e.preventDefault();
    const offset = 130;
    const top = target.getBoundingClientRect().top + window.scrollY - offset;
    window.scrollTo({ top, behavior: 'smooth' });
  }

  _observe(h2s) {
    const links = Array.from(this._tocList.querySelectorAll('a'));
    const linkByHref = {};
    links.forEach(a => { linkByHref[a.getAttribute('href')] = a; });

    const obs = new IntersectionObserver((entries) => {
      const visible = entries.filter(e => e.isIntersecting).sort((a, b) =>
        a.boundingClientRect.top - b.boundingClientRect.top
      );
      if (visible.length === 0) return;
      const id = visible[0].target.id;
      links.forEach(a => a.removeAttribute('data-active'));
      const active = linkByHref['#' + id];
      if (active) active.setAttribute('data-active', '');
    }, { rootMargin: '-130px 0px -50% 0px', threshold: 0 });

    h2s.forEach(h2 => obs.observe(h2));
    this._observer = obs;
  }

  disconnectedCallback() {
    if (this._observer) this._observer.disconnect();
  }
}

if (!customElements.get('policy-toc')) {
  customElements.define('policy-toc', PolicyToc);
}
